import React, { useState, useMemo } from 'react';
import { eachDayOfInterval, format, isSameDay } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import TablePeriod from 'components/FinancialReportLayout/TablePeriod';
import Button from 'components/ui-kits/Button';
import { CalendarIcon, DownloadIcon } from 'components/ui-kits/Icon';
import { Grid, GridItem } from 'components/ui-kits/Grid';
import {
  BlockWrapper,
  ButtonWrapper,
  CustomPeriodStyle,
  DateWrapper
} from './ByCustomPeriod.style';
import CustomDatePicker from 'components/ui-kits/CustomDatePicker';
import DropdownFinancial from 'components/FinancialReportLayout/DropdownFinancial';
import Loader from 'components/Loader';
import {
  downLoadFileNameFinancialReport,
  downLoadPdfFile,
  transformDateToISO
} from 'utils';
import {
  financialDownloadPDFV2,
  getFinancialReportV2
} from 'services/financialReportService';
import ActionButton from '../ActionButton';
import type { Merchant, SubscriptionPlanDetail } from 'types/Merchant';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';

interface ByCustomPeriodProps {
  setDisplayMode(mode: 'month' | 'period'): void;
  subscriptionPlans: SubscriptionPlanDetail[];
  merchant?: Merchant;
}

const ByCustomPeriod = ({
  setDisplayMode,
  subscriptionPlans,
  merchant
}: ByCustomPeriodProps) => {
  const { currentPlan, initialStartDate, initialEndDate } =
    useSubscriptionPlan(subscriptionPlans);
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const isAccountingReport = merchant?.isAccountingReport;

  const includeDates = useMemo(() => {
    if (isAccountingReport) return undefined;

    if (startDate && !endDate) {
      const foundPlan = subscriptionPlans.find((plan) =>
        eachDayOfInterval({
          start: new Date(plan.startDate),
          end: new Date(plan.endDate)
        }).some((date) => isSameDay(date, startDate))
      );

      return foundPlan
        ? eachDayOfInterval({
            start: startDate,
            end: new Date(foundPlan.endDate)
          })
        : undefined;
    }

    return subscriptionPlans.flatMap(({ startDate, endDate }) =>
      eachDayOfInterval({
        start: new Date(startDate),
        end: new Date(endDate)
      })
    );
  }, [isAccountingReport, subscriptionPlans, startDate, endDate]);

  const { data, isLoading } = useQuery(
    ['getByPeriod'],
    ({ signal }) =>
      getFinancialReportV2({
        filter: 'by_month_period',
        startDate: transformDateToISO(startDate),
        endDate: transformDateToISO(endDate),
        signal
      }),
    {
      enabled: Boolean(startDate && endDate)
    }
  );

  const { isLoading: isLoadingDownload, mutate } = useMutation(
    financialDownloadPDFV2,
    {
      onSuccess: (blob: any, data: any) => {
        const startDate = format(new Date(data?.startDate), 'dd_MM_yyyy');
        const endDate = format(new Date(data?.endDate), 'dd_MM_yyyy');
        downLoadPdfFile(
          blob,
          downLoadFileNameFinancialReport(startDate, endDate)
        );
      }
    }
  );

  const handleDownloadPDF = () => {
    if (startDate && endDate) {
      const start = transformDateToISO(startDate);
      const end = transformDateToISO(endDate);
      mutate({ startDate: start, endDate: end });
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <CustomPeriodStyle>
        <Grid col={11} start="large" marginRight={true}>
          <GridItem colStart={2} colEnd={10} nameStart="large" nameEnd="gutter">
            <BlockWrapper>
              <DropdownFinancial
                mode="period"
                setDisplayMode={setDisplayMode}
              />
              <DateWrapper>
                <CustomDatePicker
                  selected={startDate}
                  onChange={(dates: (Date | undefined)[]) => {
                    const [start, end] = dates;

                    setStartDate(start as Date);
                    setEndDate(end as Date);

                    if (end && start) {
                      queryClient.invalidateQueries('getByPeriod');
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  variant="round"
                  icon={<CalendarIcon />}
                  selectsRange
                  includeDates={includeDates}
                />
              </DateWrapper>
              <ButtonWrapper>
                <Button
                  className="download"
                  icon={<DownloadIcon />}
                  isUpperCase={false}
                  isFluid={false}
                  onClick={handleDownloadPDF}>
                  Télécharger (PDF)
                </Button>
                <ActionButton
                  planType="financial_report"
                  currentPlan={currentPlan}
                  isAccountingReport={isAccountingReport}
                />
              </ButtonWrapper>
            </BlockWrapper>
          </GridItem>
        </Grid>
        {data && (
          <TablePeriod
            dataPeriod={data}
            startMonth={format(new Date(startDate), 'LLL')}
            endMonth={format(new Date(endDate), 'LLL')}
          />
        )}
        {isLoadingDownload && <Loader />}
      </CustomPeriodStyle>
    </>
  );
};

export default ByCustomPeriod;
